import { Vals } from '~lib';

export namespace AgentMode {
  /**
   * @desc Selectable agent personas. Each mode tunes the model, reasoning depth,
   * and output style on the backend (see backend agent `modes.ts`). The string
   * values are the wire contract shared by the tRPC router, portal, and backend.
   */
  export const Modes = {
    /** @desc Full analytical depth — charts, reports, presentable artifacts. */
    Analyst: 'analyst',
    NoFuss: 'no-fuss',
  } as const;

  export type T = Vals<typeof Modes>;

  /**
   * @desc Backend safety-net only: the mode used to coerce legacy/absent stored
   * values. The *user-facing* default is driven by the frontend store, which
   * defaults new conversations to No-fuss.
   */
  export const DEFAULT: T = Modes.Analyst;

  export const ALL = Object.values(Modes) as [T, ...T[]];

  /**
   * @desc Human-facing labels for UI controls (e.g. the mode picker). The
   * Analyst mode is surfaced as "Default" for now — it is the original
   * full-analysis behaviour. Higher modes will be split out later.
   */
  export const labels: Record<T, string> = {
    [Modes.Analyst]: 'Default',
    [Modes.NoFuss]: 'No Fuss',
  };

  /** @desc Short descriptions surfaced as tooltips/help text in the UI. */
  export const descriptions: Record<T, string> = {
    [Modes.Analyst]: 'Full analysis with charts, reports, and presentable artifacts.',
    [Modes.NoFuss]: 'Fast one-line answers. No reasoning, no frills.',
  };

  export const isMode = (value: unknown): value is T => ALL.includes(value as T);

  /**
   * @desc Maps any stored value to a current mode, falling back to the default.
   * Guards legacy documents (e.g. the retired `'reporter'`/`'default'` values)
   * and absent fields — both resolve to the default behaviour.
   */
  export const coerce = (value: unknown): T => (isMode(value) ? value : DEFAULT);
}
